// **React Imports
import { memo, useCallback, useMemo, useState } from 'react'

// **MUI Imports
import {
  Box,
  CircularProgress,
  LinearProgress,
  linearProgressClasses,
  MenuItem,
  Popover,
  styled,
  Typography
} from '@mui/material'

// **API Imports
import { getChallengesOfLinearityScore } from '../../../../../../../axios/api/ai-evaluations'

// **Component Imports
import Icon from '../../../../../../../components/icon'

// **Utils Imports
import { CHALLENGE_TYPE_ICON } from '../../../../../../../utils/constant'
import { showErrorToast } from '../../../../../../../utils/utils'

// **SCSS Imports
import '../index.scss'

// **helper component
const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 7,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[300]
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: 'var(--badge-color)'
  }
}))

const LinearityScoreComponent = ({
  acquired,
  total,
  challengeType,
  attemptId = null,
  forChallengeRow = false
}) => {
  // states
  const [previewPopoverData, setPreviewPopoverData] = useState({
    anchorEl: null,
    loading: null,
    data: null,
    typeId: null
  })

  // variables
  const percentage = useMemo(
    () => (total > 0 ? Math.floor((acquired / total) * 100) : 0),
    [acquired, total]
  )

  // handlers
  const handleChallengePreview = useCallback(
    async (e) => {
      setPreviewPopoverData({
        anchorEl: e?.currentTarget || null,
        data: null,
        loading: true,
        typeId: null
      })

      const response = await getChallengesOfLinearityScore(attemptId)

      if (response?.isError) {
        setPreviewPopoverData((prev) => ({
          anchorEl: null,
          data: null,
          loading: false,
          typeId: null
        }))
        showErrorToast(response)
      } else {
        setPreviewPopoverData((prev) => ({
          ...prev,
          data: response,
          loading: false,
          typeId: null
        }))
      }
    },
    [attemptId]
  )

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          cursor: !forChallengeRow ? 'pointer' : 'default'
        }}
        onClick={!forChallengeRow ? handleChallengePreview : () => {}}
        title={`Linearity Scoring Summary`}
      >
        <Icon
          icon={'tdesign:ai-chart-bar'}
          style={{ fontSize: '20px', color: 'var(--primary-main)' }}
        />
        <Box
          className={`linearity-score-badge ${
            percentage > 70
              ? 'low-grades'
              : percentage > 40
                ? 'medium-grades'
                : 'high-grades'
          }`}
        >
          <Box className="score-count">
            <Typography component={'span'} className="acquired-marks">
              {acquired}
            </Typography>
            <Typography component={'span'} className="total-marks">
              /{total}
            </Typography>
          </Box>
          <BorderLinearProgress
            className="score-progress"
            variant="determinate"
            value={percentage}
          />
        </Box>
        {!forChallengeRow && (
          <Icon
            icon={'mdi:chevron-down'}
            style={{ fontSize: '20px', color: 'var(--text-secondary)' }}
          />
        )}
      </Box>

      <Popover
        open={Boolean(previewPopoverData?.anchorEl)}
        anchorEl={previewPopoverData?.anchorEl}
        onClose={() => {
          setPreviewPopoverData({
            anchorEl: null,
            data: null,
            typeId: null,
            loading: false
          })
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        {
          <Box
            sx={{
              padding: '8px 4px',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              maxWidth: '550px',
              width: 'max-content',
              height: '100%',
              maxHeight: '200px',
              overflowY: 'auto'
            }}
          >
            {!previewPopoverData?.loading && (
              <Typography
                variant="body2"
                sx={{ padding: '4px 8px', display: 'flex', gap: '16px' }}
              >
                <Icon
                  icon={
                    CHALLENGE_TYPE_ICON?.[challengeType] ||
                    CHALLENGE_TYPE_ICON?.APPLICATION
                  }
                />
                Challenges
              </Typography>
            )}
            {previewPopoverData?.loading ? (
              <Box
                sx={{
                  width: '400px',
                  height: '150px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              previewPopoverData?.data?.map((challenge, index) => {
                return (
                  <MenuItem
                    key={challenge?.id || `${index}-challenge`}
                    className="item "
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'default'
                    }}
                    title="Linearity Scoring Summary"
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        flex: 1
                      }}
                    >
                      <Typography variant="body2">{index + 1}.</Typography>
                      <Typography
                        title={challenge?.name}
                        sx={{
                          width: '300px',
                          display: 'block',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden'
                        }}
                      >
                        {challenge?.name}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '24px',
                        alignItems: 'center'
                      }}
                      className={`ai-score-badge ${
                        (challenge?.acquired || 0) > 70
                          ? 'low-grades'
                          : (challenge?.acquired || 0) > 40
                            ? 'medium-grades'
                            : 'high-grades'
                      }`}
                    >
                      <Box className="score-count">
                        <Typography
                          component={'span'}
                          className="acquired-marks"
                        >
                          {challenge?.acquired || 0}
                        </Typography>
                        <Typography component={'span'} className="total-marks">
                          /{challenge?.outOfScore || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                )
              })
            )}
          </Box>
        }
      </Popover>
    </>
  )
}

export default memo(LinearityScoreComponent)
